import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { debounceTime, map, takeUntil } from 'rxjs/operators';

import { filterBoolean } from '@recruitee/common';
import { OAuthClientAuth, OAuthCounters } from '@recruitee/http/types';
import { I18nService } from '@recruitee/i18n';
import {
  OAuthAuthorizeActions,
  OAuthClientAuthActions,
  OAuthClientAuthSelectors,
  OAuthCountersActions,
  OAuthCountersSelectors,
} from '@recruitee/oauth/data';
import { DialogService } from '@recruitee/user-interface';

import { IntegrationEventActions } from '../../common/events/events';
import { IntegrationLegalDialogService } from '../../misc/integration-list-legal-dialog/integration-legal-dialog.service';
import { ZapierIntegrationComponent } from '../zapier-integration/zapier-integration.component';

@Component({
  selector: 'rt-zapier-container',
  templateUrl: './zapier-container.component.html',
  standalone: true,
  imports: [ZapierIntegrationComponent],
})
export class ZapierContainerComponent implements OnInit, OnDestroy {
  @ViewChild(ZapierIntegrationComponent, { read: ZapierIntegrationComponent, static: true })
  public zapierIntegration: ZapierIntegrationComponent;
  public kind = 'zapier';

  public authorizedClientsLoading = false;
  public authorizedClientsLoaded = false;
  public authorizedClientsCounters: OAuthCounters | null = null;
  public authorizedClients: Record<string, OAuthClientAuth> | null = null;

  public removeActionConfig = {
    handler: () => {
      this.store.dispatch(
        OAuthAuthorizeActions.deauthorize({
          payload: { clientId: this.authorizedClients?.zapier.client.id! },
        }),
      );
      this.store.dispatch(
        IntegrationEventActions.IntegrationRemovedEvent({ payload: { uid: this.kind } }),
      );
    },
    dialogConfig: {
      title: this.i18n.translate(
        'app.settings.integrations.zapier.marketplace_remove_dialog.title',
      ),
      variant: 'error',
      agreeLabel: this.i18n.translate('app.settings.integrations.slack.remove_dialog.agree_label'),
      message: this.i18n.translate(
        'app.settings.integrations.zapier.marketplace_remove_dialog.message',
      ),
      safeFooter: true,
      cancelLabel: this.i18n.translate('rti.common.cancel'),
    },
  };

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private cdRef: ChangeDetectorRef,
    private i18n: I18nService,
    private dialogService: DialogService,
    private integrationLegalDialogService: IntegrationLegalDialogService,
  ) {}

  public ngOnInit(): void {
    combineLatest({
      isLoaded: this.store.select(OAuthClientAuthSelectors.isLoaded),
      isPending: this.store.select(OAuthClientAuthSelectors.isPending),
      data: this.store.select(OAuthClientAuthSelectors.data).pipe(
        map(clientAuths =>
          (clientAuths || []).reduce((acc, clientAuth) => {
            acc[clientAuth.client.name] = clientAuth;
            return acc;
          }, <Record<string, OAuthClientAuth>>{}),
        ),
        filterBoolean,
      ),
    })
      .pipe(debounceTime(0), takeUntil(this.ngOnDestroy$))
      .subscribe(({ isLoaded, isPending, data }) => {
        this.authorizedClientsLoaded = isLoaded;
        this.authorizedClientsLoading = isPending;
        this.authorizedClients = data;
        this.cdRef.detectChanges();
      });

    this.store
      .select(OAuthCountersSelectors.data)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(data => {
        this.authorizedClientsCounters = data;
      });

    this.reloadAuthorizedClients();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public remove(): void {
    this.dialogService.warning(this.removeActionConfig.dialogConfig).subscribe(confirmed => {
      if (!confirmed) return;
      this.removeActionConfig.handler();
    });
  }

  public integrate(): void {
    this.integrationLegalDialogService
      .openLegalDialog(
        null,
        this.i18n.translate('app.settings.integrations.zapier.name'),
        undefined,
        {
          helpArticleUrl: this.i18n.translate('app.settings.integrations.zapier.help_article_link'),
        },
      )
      .subscribe(result => {
        if (result && result.accepted) {
          this.zapierIntegration.authorize();
        }
      });
  }

  public reloadAuthorizedClients(): void {
    this.store.dispatch(OAuthClientAuthActions.fetch());
    this.store.dispatch(OAuthCountersActions.fetch());
  }
}
